import { showPwaUpdateStatus } from './pwaUpdateStatus';
const BUILD_STORAGE_KEY = 'vo2_pwa_build_id';
const RECOVERY_SESSION_KEY = 'vo2_pwa_recovery_reload';

/**
 * Detect stale Home Screen installs (old index.html + deleted /assets hashes).
 * Clears SW + Cache Storage once, then reloads so fresh index.html + CSS load.
 */
export async function runPwaBuildRecovery(): Promise<void> {
  if (!import.meta.env.PROD) return;
  if (typeof window === 'undefined') return;
  if (sessionStorage.getItem(RECOVERY_SESSION_KEY) === '1') return;

  const buildId = import.meta.env.VITE_BUILD_ID as string | undefined;
  const storedBuild = localStorage.getItem(BUILD_STORAGE_KEY);

  let staleEntry = false;
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}index.html`, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (res.ok) {
      const html = await res.text();
      const liveMatch = html.match(/\/assets\/(index-[^"']+\.js)/);
      const currentSrc = document.querySelector('script[src*="/assets/index-"]')?.getAttribute('src') ?? '';
      const currentFile = currentSrc.split('/').pop() ?? '';
      staleEntry = Boolean(liveMatch?.[1] && currentFile && liveMatch[1] !== currentFile);
    }
  } catch {
    /* offline — skip network compare */
  }

  const buildChanged = Boolean(buildId && storedBuild && storedBuild !== buildId);
  const cssBroken = document.querySelectorAll('link[rel="stylesheet"]').length > 0
    && !hasUsableStylesheet();

  if (!staleEntry && !buildChanged && !cssBroken) {
    if (buildId) localStorage.setItem(BUILD_STORAGE_KEY, buildId);
    return;
  }

  showPwaUpdateStatus();
  sessionStorage.setItem(RECOVERY_SESSION_KEY, '1');
  await clearPwaCachesAndServiceWorkers();
  if (buildId) localStorage.setItem(BUILD_STORAGE_KEY, buildId);
  window.location.reload();
}

function hasUsableStylesheet(): boolean {
  try {
    for (const sheet of Array.from(document.styleSheets)) {
      if (!sheet.href?.includes('/assets/index-')) continue;
      const rules = sheet.cssRules;
      if (!rules?.length) return false;
      for (const rule of Array.from(rules)) {
        if (rule instanceof CSSStyleRule && rule.selectorText?.includes('.wb-mobile-palette-scroll')) {
          return true;
        }
      }
      return false;
    }
  } catch {
    /* cross-origin or blocked */
  }
  return false;
}

async function clearPwaCachesAndServiceWorkers(): Promise<void> {
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((reg) => reg.unregister()));
  }
}
